import type { GetContextMenuItemsParams, GridOptions, IRowNode } from 'ag-grid-community';
import { compact, get, identity } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { map, pipe } from 'rxjs';
import { useTheme } from 'styled-components';
import { useCallbackRef, useConstant, useWSFilterPipe } from '../../hooks';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../utils';
import {
  agGridMenuSeparator,
  baseTreeGroupColumnDef,
  BLOTTER_SELECTION_MULTI_PARAMS_WITH_CHECKBOXES,
  BlotterDensity,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  DEFAULT_BLOTTER_SELECTION_READONLY_PARAMS,
  filterExistsAndExcludes,
  getRowNodesToOperateOn,
  removeEmptyFilters,
  useGenericFilter,
  usePersistedBlotterTable,
  type AgGridMenuItems,
  type ColumnDef,
  type CompositePipeFunction,
  type ContextMenuOptions,
  type GenericFilter,
  type UseBlotterTableProps,
} from '../BlotterTable';
import { useEntityAdminHeaderLayout } from './components/EntityAdminHeaderLayoutContext';
import { ENTITY_INTERNAL_ROW_ID, EntityAdminClass } from './EntityAdminClass';
import { getEntityAdminSelectionContextMenuItems } from './getEntityAdminSelectionContextMenuItems';
import { resolveEntityAdminExportOptions } from './resolveEntityAdminExportOptions';
import type { EntityAdminBlotterProps, EntityAdminContextMenuContext, EntityAdminRecord } from './types';
import {
  applyInheritanceCellStyle,
  getAddChildEntityColumn,
  getDeleteColumn,
  getEditColumn,
  getModeColumn,
  onlyServerFilterEntries,
  resolveAllowAddEntity,
} from './utils';
import { useEntityAdminTabsContext } from './wrappers/EntityAdminTabsWrapper';

export const useEntityAdminBlotterProps = <TRecord extends EntityAdminRecord, TDrawerRecord extends EntityAdminRecord>(
  props: Omit<EntityAdminBlotterProps<TRecord, TDrawerRecord>, 'getEntityAsDrawerEntity' | 'renderDrawer'> & {
    getEntityAsDrawerEntity: NonNullable<EntityAdminBlotterProps<TRecord, TDrawerRecord>['getEntityAsDrawerEntity']>;
  }
) => {
  const {
    childIDField,
    entityIDField,
    filterableProperties,
    filterableServerFields = EMPTY_ARRAY,
    allowModeSwitch = false,
    allowEditEntity = false,
    suppressEditEntityRowButton = false,
    groupColumnDef: groupColumnDefLocal = EMPTY_OBJECT,
    getCustomContextMenuItems: getCustomContextMenuItemsFromProps,
    density = BlotterDensity.Comfortable,
    allowAddEntity,
    allowDeleteEntity = false,
    modeSwitchConfigurationInternal,
    allowBulkEdit = false,
    allowBulkSelect = false,
    bulkEditFields = EMPTY_ARRAY,
    suppressBulkDelete,
    maxBulkEditRows = 10_000,
    isRequesting = false,
    allowPauseResume = false,
    onAddChildEntity,
    childEntityName,
    persistKey: persistKeyLocal = null,
    openEntityAdminDrawer,
    getEntityAsDrawerEntity,
    closeEntityAdminDrawer,
    isEntityAdminDrawerOpen,
    updateClassEntitiesWithConfirmation,
    deleteClassEntitiesWithConfirmation,
    selectedEntities,
    updateSelectedEntities,
    parentMapRef,
    serverDataToRecordMappingFunc = identity as (entity: unknown) => TRecord,
    clientLocalFilter,
    blotterTableContext,
    entityName,
    title,
    suppressExport,
    exportOptions: exportOptionsFromProps,
    isRowBulkEditSelectable,
    allowInheritance,
    onFilterChanged,
    initialGroupsExpanded,
  } = props;
  const theme = useTheme();
  const { isPanelLayout } = useEntityAdminHeaderLayout();
  const { child: allowAddChild } = resolveAllowAddEntity(allowAddEntity);

  const handleEditRowClick = useCallbackRef(
    ({ data: entityClass, node }: { data?: EntityAdminClass<TRecord>; node: IRowNode<EntityAdminClass<TRecord>> }) => {
      if (
        entityClass != null &&
        !suppressEditEntityRowButton &&
        (allowEditEntity === true || (allowEditEntity !== false && !allowEditEntity.isDisabled?.(entityClass.data)))
      ) {
        openEntityAdminDrawer({ setupEntities: [entityClass], addingChildEntity: false });
        node.setSelected(true, true);
      }
    }
  );

  const [selectAllEntityType, setSelectAllEntityType] = useState<'parents' | 'children' | null>('parents');
  type OnSelectionChanged = NonNullable<GridOptions<EntityAdminClass<TRecord>>['onSelectionChanged']>;
  const onSelectionChanged = useCallbackRef<OnSelectionChanged>(params => {
    if (params.source.includes('api')) {
      updateSelectedEntities(params.api.getSelectedRows());
      return;
    }
    const selectedChildrenNodes = getRowNodesToOperateOn(params).filter(node => node.data?.isChildRow);
    if (childIDField == null) {
      // If childIDField is not provided, the data is not hierarchical.
      params.api.setNodesSelected({ nodes: selectedChildrenNodes, newValue: true });
    } else {
      const selectedParentNodes = getRowNodesToOperateOn(params, 0).filter(node => node.data?.isParentRow);
      if (selectedChildrenNodes.length > 0 && selectedParentNodes.length > 0) {
        // Resolve crash - children and parent rows cannot be selected at the same time
        switch (params.source) {
          case 'uiSelectAllFiltered': {
            // Select all logic, toggles between parent rows, child rows, and no rows selected
            if (selectAllEntityType === 'parents') {
              params.api.deselectAll();
              params.api.setNodesSelected({ nodes: selectedParentNodes, newValue: true });
              setSelectAllEntityType('children');
            } else if (selectAllEntityType === 'children') {
              params.api.deselectAll();
              params.api.setNodesSelected({ nodes: selectedChildrenNodes, newValue: true });
              params.api.expandAll();
              setSelectAllEntityType(null);
            } else if (selectAllEntityType == null) {
              params.api.deselectAll();
              closeEntityAdminDrawer();
              setSelectAllEntityType('parents');
            }
            break;
          }
          default: {
            const newSelectionHasChildRow = [...selectedChildrenNodes, ...selectedParentNodes]
              // Only consider newly selected nodes
              .filter(node => !selectedEntities.includes(node.data!))
              .some(node => node.data?.isChildRow);

            if (newSelectionHasChildRow) {
              // New selection includes a child row, deselect all parent rows
              params.api.setNodesSelected({ nodes: selectedChildrenNodes, newValue: true });
              params.api.setNodesSelected({ nodes: selectedParentNodes, newValue: false });
            } else {
              // New selection includes a parent row, deselect all child rows
              params.api.setNodesSelected({ nodes: selectedParentNodes, newValue: true });
              params.api.setNodesSelected({ nodes: selectedChildrenNodes, newValue: false });
            }

            // Update the select all entity type based on the new selection type
            setSelectAllEntityType(newSelectionHasChildRow ? 'children' : 'parents');
            break;
          }
        }
      } else if (selectedChildrenNodes.length > 0 && selectedParentNodes.length === 0) {
        if (params.source === 'uiSelectAllFiltered') {
          // [DEAL-5131] TODO: This is a workaround UX for when all of our parent rows being "unselectable" due to missing data.
          // In this instance, we need to expand all rows when clicking select all or the UX is very confusing.
          params.api.expandAll();
        }
      }
    }
    const selectedClasses = params.api.getSelectedRows();
    updateSelectedEntities(selectedClasses);
    // Bulk-select mode keeps drawer editing independent from grid multi-selection.
    if (allowBulkSelect) {
      return;
    }
    // If the entity admin drawer is open, update the selected classes in the drawer.
    if (isEntityAdminDrawerOpen) {
      if (selectedClasses.length > 0) {
        openEntityAdminDrawer({
          setupEntities: selectedClasses,
          addingChildEntity: false,
        });
      } else {
        // If no classes are selected, close the drawer.
        closeEntityAdminDrawer();
      }
    }
  });

  const getDataPath = useCallbackRef((data: TRecord): TRecord[keyof TRecord][] => {
    // If childIDField is provided, the data is hierarchical.
    if (childIDField != null && get(data, childIDField)) {
      return [get(data, entityIDField), get(data, childIDField)] satisfies TRecord[keyof TRecord][];
    }
    // If childIDField is provided but the childIDField is null, the row is a parent row.
    return [get(data, entityIDField)] satisfies TRecord[keyof TRecord][];
  });

  const startingColumns = useMemo(() => {
    const colDefs: ColumnDef<TRecord>[] = [];

    if (modeSwitchConfigurationInternal != null) {
      colDefs.push(
        getModeColumn<TRecord>({
          handleOnSwitchMode: (entityClass, newMode) => {
            const newObject = Object.assign({}, entityClass.data, {
              [modeSwitchConfigurationInternal.modeField]: newMode,
            });
            return updateClassEntitiesWithConfirmation([entityClass], newObject);
          },
          modeField: modeSwitchConfigurationInternal.modeField,
          isDisabled: (entity: TRecord) =>
            allowModeSwitch === true ? false : (modeSwitchConfigurationInternal.isDisabled?.(entity) ?? false),
          isHidden: (entity: TRecord) =>
            allowModeSwitch === true ? false : (modeSwitchConfigurationInternal.isHidden?.(entity) ?? false),
        })
      );
    }

    return colDefs;
  }, [modeSwitchConfigurationInternal, updateClassEntitiesWithConfirmation, allowModeSwitch]);

  const endingColumns = useMemo(() => {
    const colDefs: ColumnDef<TRecord>[] = [];

    if (allowAddChild && childIDField != null) {
      colDefs.push(
        getAddChildEntityColumn<TRecord>({
          openEntityAdminDrawer: openEntityAdminDrawer,
          onAddChildEntity,
          childEntityName,
          theme,
        })
      );
    }

    if (allowEditEntity && !suppressEditEntityRowButton) {
      colDefs.push(
        getEditColumn<TRecord>({
          handleOnClick: entityClass => {
            openEntityAdminDrawer({ setupEntities: [entityClass], addingChildEntity: false });
          },
          isDisabled: entityClass =>
            allowEditEntity === true ? false : (allowEditEntity.isDisabled?.(entityClass.data) ?? false),
          isHidden: entityClass =>
            allowEditEntity === true ? false : (allowEditEntity.isHidden?.(entityClass.data) ?? false),
          isDisabledTooltip: allowEditEntity === true ? undefined : allowEditEntity.isDisabledTooltip,
          isPanelLayout,
          theme,
        })
      );
    }

    if (allowDeleteEntity) {
      colDefs.push(
        getDeleteColumn<TRecord>({
          handleOnClickDelete: entityClass => {
            deleteClassEntitiesWithConfirmation([entityClass]);
          },
          isDisabled: entityClass =>
            allowDeleteEntity === true ? false : (allowDeleteEntity.isDisabled?.(entityClass.data) ?? false),
          isHidden: entityClass =>
            allowDeleteEntity === true ? false : (allowDeleteEntity.isHidden?.(entityClass.data) ?? false),
          isDisabledTooltip: allowDeleteEntity === true ? undefined : allowDeleteEntity.isDisabledTooltip,
        })
      );
    }

    if (colDefs.length > 0) {
      const fillerWidth = isPanelLayout ? theme.spacing12 : theme.spacing24;
      colDefs.push({
        type: 'filler',
        id: 'filler',
        pinned: 'right',
        frozen: true,
        flex: 0,
        width: fillerWidth,
        minWidth: fillerWidth,
        maxWidth: fillerWidth,
      });
    }

    return colDefs;
  }, [
    allowAddChild,
    childIDField,
    allowEditEntity,
    allowDeleteEntity,
    openEntityAdminDrawer,
    onAddChildEntity,
    childEntityName,
    deleteClassEntitiesWithConfirmation,
    suppressEditEntityRowButton,
    isPanelLayout,
    theme,
  ]);

  const getUniqueKey = useCallbackRef((entity: EntityAdminClass<TRecord>) => entity[ENTITY_INTERNAL_ROW_ID]);

  /**
   * This is the list of columns that are passed in as the default columns for usePersistedBlotterTable
   * This list is not allowed to be modified past the initial render.
   */
  const styledColumns = useConstant([
    ...startingColumns,
    ...props.columns.map(applyInheritanceCellStyle),
    ...endingColumns,
  ]);

  const tabsContext = useEntityAdminTabsContext();
  const persistKey =
    tabsContext && persistKeyLocal ? `${persistKeyLocal}/${tabsContext.selectedTab.id}` : persistKeyLocal;
  const persistedTable = usePersistedBlotterTable(persistKey, undefined, {
    persistFilter: persistKey != null,
  });

  const handleFilterChanged = useCallback(
    (filter: GenericFilter) => {
      persistedTable.onFilterChanged(filter);
      onFilterChanged?.(filter);
    },
    [persistedTable, onFilterChanged]
  );

  const filterResults = useGenericFilter(filterableProperties, {
    initialFilter: persistedTable.initialState?.filter,
    onFilterChanged: handleFilterChanged,
  });

  useEffect(() => {
    if (onFilterChanged == null) {
      return;
    }
    // Always report the resolved initial filter on mount (even when empty), so consumers relying on
    // onFilterChanged to derive state (e.g. a required counterparty selection) reset correctly when a
    // new tab/view mounts with no persisted filter, instead of keeping a stale value from a prior tab.
    const initialFilter = (persistedTable.initialState?.filter as GenericFilter | undefined) ?? {};
    onFilterChanged(removeEmptyFilters(initialFilter));
  }, [persistedTable.initialState?.filter, onFilterChanged]);

  const filterFunc = useCallback(
    (entity: EntityAdminClass<TRecord>) => {
      let filteredOut = false;
      filterableProperties.forEach(property => {
        if (property.field == null) {
          throw new Error('Field is required for all filterable properties of EntityAdmin');
        }
        if (property.control !== 'text' && property.fieldFilter != null) {
          // If the property has a fieldFilter, use it to deside if the entity should be filtered out.
          const filterPart = filterResults.filter[property.key];
          if (!(filterPart && filterPart instanceof Array)) {
            return false;
          }
          // For EntityAdmin, the filtered field should be derived including inheritance.
          filteredOut = filteredOut || !property.fieldFilter(get(entity, property.field), filterPart);
        } else if (
          filterExistsAndExcludes(
            filterResults.filter,
            property.key,
            // For EntityAdmin, the filtered field should be derived including inheritance
            { ...entity.data, [property.field]: get(entity, property.field) },
            property.field
          )
        ) {
          filteredOut = true;
        }
      });
      return !filteredOut;
    },
    [filterResults.filter, filterableProperties]
  );

  const userFilterFunc = useMemo(() => props.userFilterFunc ?? (() => true), [props.userFilterFunc]);
  const serverDataMapFunc = useCallbackRef(serverDataToRecordMappingFunc);
  const filterPipe = useWSFilterPipe<EntityAdminClass<TRecord>>({ getUniqueKey, filterFunc });
  const blotterTablePipe: CompositePipeFunction<EntityAdminClass<TRecord>, TRecord> = useConstant(
    pipe(
      // Step 1: Remove any rows by the user provided function
      map(json => ({ ...json, data: json.data.map(serverDataMapFunc).filter(item => userFilterFunc(item)) })),
      // Step 2: Update parentMapRef with new data
      map(json => {
        if (childIDField != null) {
          json.data.forEach(data => {
            if (get(data, childIDField) == null) {
              parentMapRef.current.set(get(data, entityIDField), data);
            }
          });
        }
        return json;
      }),
      // Step 3: All items must have an entityID field and then map to EntityAdminClass
      map(json => ({
        ...json,
        data: json.data
          .filter(item => !!item[entityIDField])
          .map(row => new EntityAdminClass<TRecord>(row, entityIDField, childIDField, parentMapRef, allowInheritance)),
      })),
      filterPipe
    )
  );

  const treeDataProps: Pick<GridOptions<any>, 'autoGroupColumnDef' | 'treeData' | 'getDataPath'> | undefined =
    useMemo(() => {
      if (childIDField != null) {
        // If childIDField is provided, the data is hierarchical.
        return {
          autoGroupColumnDef: {
            ...baseTreeGroupColumnDef,
            ...groupColumnDefLocal,
            cellRendererParams: {
              ...baseTreeGroupColumnDef?.cellRendererParams,
              ...groupColumnDefLocal.cellRendererParams,
            },
          },
          treeData: true,
          getDataPath: (entity: EntityAdminClass<TRecord>) => getDataPath(entity.data),
        };
      }
      return undefined;
    }, [childIDField, getDataPath, groupColumnDefLocal]);

  const serverFieldsFilter: GenericFilter = useMemo(() => {
    return onlyServerFilterEntries(
      filterResults.filter,
      filterableProperties.filter(p => filterableServerFields.includes(p.field!))
    );
  }, [filterResults.filter, filterableProperties, filterableServerFields]);

  const getCustomContextMenuItems = useCallbackRef(
    (params: GetContextMenuItemsParams<EntityAdminClass<TRecord>>): AgGridMenuItems<EntityAdminClass<TRecord>> => {
      const enhancedParams = {
        ...params,
        context: {
          ...params.context,
          entityAdmin: {
            openEntityAdminDrawer,
            deleteClassEntitiesWithConfirmation: allowDeleteEntity ? deleteClassEntitiesWithConfirmation : undefined,
            entityIDField,
            childIDField,
          },
        } satisfies EntityAdminContextMenuContext<TRecord>,
      };

      const pageItems = getCustomContextMenuItemsFromProps?.(enhancedParams) ?? EMPTY_ARRAY;
      const selectionItems = getEntityAdminSelectionContextMenuItems(params, {
        allowBulkEdit,
        allowEditEntity,
        allowDeleteEntity,
        suppressBulkDelete,
        bulkEditFields,
        modeSwitch: modeSwitchConfigurationInternal,
        maxBulkEditRows,
        isRequesting,
        openEntityAdminDrawer,
        updateClassEntitiesWithConfirmation,
        deleteClassEntitiesWithConfirmation,
      });

      return compact([
        ...pageItems,
        pageItems.length > 0 && selectionItems.length > 0 ? agGridMenuSeparator : undefined,
        ...selectionItems,
      ]);
    }
  );

  const contextMenuOptions: ContextMenuOptions<EntityAdminClass<TRecord>> = useMemo(
    () => ({
      dataDisplayCallback: row => row.data,
      getCustomContextMenuItems,
    }),
    [getCustomContextMenuItems]
  );

  // Width of the bulk selection column varies depending on the layout.
  const width = isPanelLayout ? 40 : 50;

  const gridOptions: UseBlotterTableProps<EntityAdminClass<TRecord>>['gridOptions'] = useMemo(
    () => ({
      ...treeDataProps,
      // -1 expands all group levels by default (still overridable by a user's own persisted expansion state).
      groupDefaultExpanded: initialGroupsExpanded ? -1 : undefined,
      rowSelection: allowBulkEdit
        ? {
            ...BLOTTER_SELECTION_MULTI_PARAMS_WITH_CHECKBOXES,
            selectAll: 'filtered',
            isRowSelectable: ({ data: entityClass }: { data: EntityAdminClass<TRecord> | undefined }) =>
              entityClass?.data != null &&
              // By default, all rows are selectable for bulk editing.
              (isRowBulkEditSelectable != null ? isRowBulkEditSelectable(entityClass.data) : true),
          }
        : allowBulkSelect
          ? {
              ...DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
              selectAll: 'filtered',
            }
          : {
              ...DEFAULT_BLOTTER_SELECTION_READONLY_PARAMS,
              selectAll: 'filtered',
            },
      ...(allowBulkEdit
        ? { selectionColumnDef: { pinned: 'left' as const, width, minWidth: width, maxWidth: width } }
        : {}),
      onRowDoubleClicked: handleEditRowClick,
      onSelectionChanged,
      // Prevent ag-grid from automatically unpinning columns when viewport is too narrow
      processUnpinnedColumns: () => EMPTY_ARRAY,
    }),
    [
      treeDataProps,
      initialGroupsExpanded,
      allowBulkEdit,
      allowBulkSelect,
      handleEditRowClick,
      onSelectionChanged,
      isRowBulkEditSelectable,
      width,
    ]
  );

  const pauseParams: UseBlotterTableProps<EntityAdminClass<TRecord>>['pauseParams'] = useMemo(
    () => ({ showPauseButton: allowPauseResume }),
    [allowPauseResume]
  );

  // Default export name (slugified title or entity fallback), hierarchical baseParams, then lodash merge with `exportOptions` prop.
  const exportOptions: UseBlotterTableProps<EntityAdminClass<TRecord>>['exportOptions'] = useMemo(
    () =>
      suppressExport
        ? undefined
        : resolveEntityAdminExportOptions({
            title,
            entityName,
            childIDField,
            exportOptions: exportOptionsFromProps,
          }),
    [suppressExport, title, entityName, childIDField, exportOptionsFromProps]
  );

  const blotterTableProps: Omit<UseBlotterTableProps<EntityAdminClass<TRecord>>, 'dataObservable'> & {
    // This conversion handled internally by us and each row is guaranteed a unique row ID
    rowID: typeof ENTITY_INTERNAL_ROW_ID;
    pipe: typeof blotterTablePipe;
    filter?: GenericFilter;
  } = useMemo(
    () => ({
      rowID: ENTITY_INTERNAL_ROW_ID,
      errorObservable: undefined,
      density,
      pipe: blotterTablePipe,
      columns: styledColumns,
      persistence: persistedTable,
      filter: serverFieldsFilter,
      gridOptions,
      pauseParams,
      clientLocalFilter,
      exportOptions,
      context: blotterTableContext,
      contextMenuOptions,
    }),
    [
      density,
      blotterTablePipe,
      persistedTable,
      serverFieldsFilter,
      gridOptions,
      pauseParams,
      clientLocalFilter,
      styledColumns,
      exportOptions,
      blotterTableContext,
      contextMenuOptions,
    ]
  );

  const sharedFiltersProps = useMemo(() => {
    return {
      ...props,
      allowBulkEdit,
      allowBulkSelect,
      childIDField,
      allowDeleteEntity,
      filterableProperties,
      allowPauseResume,
      filterResults,
      getEntityAsDrawerEntity,
    };
  }, [
    props,
    allowBulkEdit,
    allowBulkSelect,
    childIDField,
    allowDeleteEntity,
    filterableProperties,
    allowPauseResume,
    filterResults,
    getEntityAsDrawerEntity,
  ]);

  const value = useMemo(() => {
    return { blotterTableProps, sharedFiltersProps };
  }, [blotterTableProps, sharedFiltersProps]);

  return value;
};
